import { getAssetMaxLiquidationFee, getMaxLiquidationFee } from 'app/queries/decoratedSilo';
import { FC, Fragment, ReactNode } from 'react';
import { useThisDecoratedSilo } from '../../silo';
import { useActionFormContext } from './ActionForm.context';
import { BigNumber } from 'ethers';
import { isDisabledCollateral as _isDisabledCollateral } from '../../disabled-collateral/isDisabledCollateral';
import { LINKS } from '@silo/types';
import { asPrecisePct } from '@silo/utils';
import { DescriptionExternalLink, StaticDescription, TooltipDescription } from '@silo/ui';

export const UserBorrowInfo: FC = () => {
  const decoratedSilo = useThisDecoratedSilo();
  const { userLiquidationThreshold, userMaximumLTV } = decoratedSilo;
  const maxLiquidationFee = getMaxLiquidationFee(decoratedSilo);

  return (
    <BorrowInfo
      maximumLTV={userMaximumLTV}
      liquidationThreshold={userLiquidationThreshold}
      maxLiquidationFee={maxLiquidationFee}
    />
  );
};

export const AssetBorrowInfo: FC = () => {
  const { selectedAsset } = useActionFormContext();
  const maxLiquidationFee = getAssetMaxLiquidationFee(selectedAsset);

  return (
    <BorrowInfo
      maximumLTV={selectedAsset.maximumLTV}
      liquidationThreshold={selectedAsset.liquidationThreshold}
      maxLiquidationFee={maxLiquidationFee}
    />
  );
};

interface BorrowTerm {
  label: ReactNode;
  value: ReactNode;
  description: ReactNode;
  hide?: boolean;
}

interface BorrowInfoProps {
  maximumLTV: BigNumber;
  liquidationThreshold: BigNumber;
  maxLiquidationFee: BigNumber;
}

const BorrowInfo: FC<BorrowInfoProps> = ({ maximumLTV, liquidationThreshold, maxLiquidationFee }) => {
  const isDisabledCollateral = _isDisabledCollateral(maximumLTV.toBigInt());

  const terms: BorrowTerm[] = [
    {
      label: 'Max LTV',
      value: asPrecisePct(maximumLTV),
      description: (
        <div className="flex flex-col gap-2.5">
          <p>
            The maximum amount you can borrow against your collateral. Example: 80% Max LTV means you can borrow $0.80
            for every $1 of collateral.
          </p>

          <p>The percentage is different for every asset in every Silo and can be changed by governance.</p>

          <DescriptionExternalLink href={LINKS.SILOPEDIA.MAX_LTV} />
        </div>
      ),
    },
    {
      label: 'Liquidation threshold',
      description: (
        <div className="flex flex-col gap-2.5">
          <p>
            The Liquidation Threshold (LT) is the point at which your loan becomes under-collateralized and is therefore
            signaled for liquidation.
          </p>

          <p>
            Example: the LT is set at 80% and your collateral is worth $1. When the value of your borrow position grows
            to $0.86, your collateral will likely be liquidated because your loan exceeded $0.80.
          </p>

          <DescriptionExternalLink href={LINKS.SILOPEDIA.LIQUIDATION_THRESHOLD} />
        </div>
      ),
      value: asPrecisePct(liquidationThreshold),
      hide: isDisabledCollateral,
    },
    isDisabledCollateral
      ? {
          label: 'Liquidation fee',
          value: '--',
          description: <MaxLiquidationFeeDescription />,
        }
      : {
          label: 'Max liquidation fee',
          value: asPrecisePct(maxLiquidationFee),
          description: <MaxLiquidationFeeDescription />,
        },
  ].filter(({ hide = false }) => !hide);

  return (
    <dl className="flex flex-col gap-5 text-base text-shale-200">
      {/* static content - so index can be used as key */}
      {terms.map(({ label, value, description }, i) => (
        <Fragment key={i}>
          <div className="flex items-baseline justify-between">
            <dt className="flex gap-1.5 items-center">
              <span>{label}</span>
              <TooltipDescription>{description}</TooltipDescription>
            </dt>
            <dd className="font-mono font-medium text-white">{value}</dd>
          </div>

          <StaticDescription>{description}</StaticDescription>
        </Fragment>
      ))}
    </dl>
  );
};

const MaxLiquidationFeeDescription: FC = () => {
  return (
    <div className="flex flex-col gap-2.5">
      <p>The maximum percentage of your collateral that can be lost if you are liquidated.</p>

      <DescriptionExternalLink href={LINKS.SILOPEDIA.MAX_LIQUIDATION_FEE} />
    </div>
  );
};
